import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { ICGIRegistered } from 'modules/promptuary/interfaces/cgi-registered.interface';
import {
	Icgi,
	QuestionType,
} from 'modules/promptuary/interfaces/cgi.interface';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

@Component({
	selector: 'promptuary-cgi',
	templateUrl: './promptuary-cgi.component.html',
	styleUrls: ['./promptuary-cgi.component.scss'],
})
export class PromptuaryCgi implements OnInit {
	cgiQuestions: Icgi[];
	cgiAnswers: ICGIRegistered[];
	@Input() formArray: FormArray;
	@Input() type = 'page';
	@Input() mainType?: string;
	@Input() service: 'form' | 'view';
	@Input() stepStart: number;
	@Input() clientId: string;
	@Input() appointmentId: string;

	@Output() onSaving = new EventEmitter<any>();
	@Output() onSaved = new EventEmitter<any>();
	@Output() onRequired = new EventEmitter<boolean>();

	constructor(
		private notificationService: ZkNotificationService,
		private promptuaryService: PromptuaryService,
		private translateService: TranslateService,
		private actionTrackerService: ActionTrackerService
	) {}

	ngOnInit(): void {
		this[`getCGI${this.service}`]();
	}

	trackFAQ(type: 'click' | 'seen' = 'click'): void {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Promptuary,
			SUBSCOPE_TYPE.Cgi,
			ITEM_TYPE.Link,
			type === 'click' ? INTERACTION_TYPE.Click : INTERACTION_TYPE.Seen
		);
	}

	getCGIform() {
		this.promptuaryService.getCGI(this.appointmentId, this.clientId).subscribe(
			(result) => {
				this.cgiQuestions = result;
				this.onRequired.emit(result.length > 0);
				this.cgiQuestions.forEach((question) => {
					question.options = this.formatOptionsForRadioFormat(question);
					this.setFormArrayItemControl(question);
					if (question.type === 'cgi') {
						this.trackFAQ('seen');
					}
				});
			},
			({ error }) => {
				if (error.messageI18n || error.message) {
					this.notificationService.showError(
						error.messageI18n || error.message
					);
				}
			}
		);
	}

	formatOptionsForRadioFormat(question) {
		// return options with radioValue property
		return question.options.map((option) => {
			return {
				...option,
				radioValue: {
					questionId: question.id,
					optionId: option.id,
				},
			};
		});
	}

	setFormArrayItemControl(question) {
		let initialValue = null;

		if (question.lastAnswer?.id) {
			initialValue = question.options.find((option) => {
				return option.id === question.lastAnswer?.id;
			})?.radioValue;
		}

		this.formArray.push(new FormControl(initialValue, Validators.required));
	}

	getCGIview() {
		this.promptuaryService
			.getCGIAnswers(this.appointmentId, this.clientId)
			.subscribe(
				(result) => {
					this.cgiAnswers = result;
				},
				() => {
					this.notificationService.showError(
						this.translateService.instant('PROMPTUARY.ERRORS.GCI_VIEW')
					);
				}
			);
	}

	selectOption(optionIdStr: string, index: number) {
		this.onSaving.emit();
		const question = this.cgiQuestions[index];
		const optionId = parseInt(optionIdStr);

		const nextCgiQuestionElement = document.getElementById(
			'cgi_question_' + (index + 1)
		);

		if (nextCgiQuestionElement) {
			nextCgiQuestionElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			});
		}

		this.promptuaryService
			.saveCGI(question.id, optionId, this.appointmentId, this.clientId)
			.subscribe(
				() => {
					question.lastAnswer = { id: optionId };
					this.onSaved.emit();
				},
				({ error }) => {
					this.notificationService.showError(
						error.messageI18n || error.message
					);
					throw Error(`Auto save error in property: cgi.`);
				}
			);
	}

	shouldRenderCgiSeverity(question: Icgi): boolean {
		if (!question.selectedOption) {
			return false;
		}

		if (!this.mainType || !question.type) {
			return false;
		}

		if (
			this.mainType === 'psychologist' &&
			question.type === QuestionType.CGI
		) {
			return true;
		}

		if (
			this.mainType === 'psychiatrist' &&
			question.type === QuestionType.CGI
		) {
			return true;
		}

		if (
			this.mainType !== 'psychiatrist' &&
			this.mainType !== 'psychologist' &&
			question.type == QuestionType.LIFE_QUALITY
		) {
			return true;
		}

		return false;
	}
}
